import { SeriesVariables } from 'api/data/entries';
import { AggregationRule, VariableSet } from 'api/data/variables';
import { Flex } from 'components/UI/Flex';
import { InputTagWithCheckboxDropdown } from 'components/UI/Interactables/InputTagWithCheckboxDropdown';
import { RadioButton } from 'components/UI/Interactables/RadioButton';
import { Loader } from 'components/UI/Loader';
import { Modal } from 'components/UI/Modal';
import { Spacer } from 'components/UI/Spacer';
import { Typography } from 'components/UI/Typography';
import { Colors } from 'environment/theme/Colors';
import { useVariables } from 'hooks/store';
import { useExportSeries } from 'hooks/store/data/entries/useDownloadDataset';
import { useCompletedAction, useSelector } from 'hooks/utils';
import { useMemo, useState } from 'react';
import { LanguageType } from 'types/enums';

interface Props {
	onClose: () => void;
}

interface DropdownItem {
	label: string;
	value: string;
	checked: boolean;
}

export function ExportSeriesModal({ onClose }: Props) {
	const language = useSelector(state => state.ui.i18n.language);
	const dictionary = DICTIONARY[language];

	const projectId = useSelector(state => state.data.projects.projectId);
	const filtersStoreData = useSelector(state => state.data.filters.dataset);

	const [fileType, setFileType] = useState<'xlsx' | 'csv'>('xlsx');
	const [useCategoryLabels, setUseCategoryLabels] = useState(true);
	const [timezone, setTimezone] = useState<'yes' | 'no' | 'utc'>('yes');
	const [splitMultipleCategories, setSplitMultipleCategories] = useState(false);
	const [includeSystemVariables, setIncludeSystemVariables] = useState(false);
	const [includeVariableTypes, setIncludeVariableTypes] = useState(false);

	const [{ loading: loadingExportSeries, error: errorExportSeries }, exportSeries] =
		useExportSeries();

	useCompletedAction(loadingExportSeries, errorExportSeries, onClose);

	const [{ data: variablesData, loading: loadingVariables }] = useVariables({
		lazy: false
	});

	const seriesItems = useMemo((): DropdownItem[] => {
		const seriesMap = variablesData.variableSetsMap ?? {};

		return Object.values(seriesMap).map((series: VariableSet) => ({
			label: series.setLabel,
			value: series.setName,
			checked: false
		}));
	}, [variablesData]);

	const [parsedSeriesItems, setParsedSeriesItems] = useState<DropdownItem[]>(seriesItems);

	const isEmptySeriesSelection = !parsedSeriesItems.some(item => item.checked);

	const mainVariableItems = useMemo((): DropdownItem[] => {
		const items: DropdownItem[] = [];
		if (!variablesData) {
			return items;
		}

		const variablesMap = variablesData.variablesMap ?? {};
		const groupsMap = variablesData.groupsMap ?? {};
		const seriesMap = variablesData.variableSetsMap ?? {};
		const order = variablesData.order ?? [];

		order.forEach(orderItem => {
			if ('variable' in orderItem) {
				const variable = variablesMap[orderItem.variable];
				if (variable) {
					items.push({
						label: variable.label,
						value: variable.name,
						checked: false
					});
				}
			} else if ('group' in orderItem) {
				const group = groupsMap[orderItem.group];
				if (group?.variablesBelongingToGroup) {
					group.variablesBelongingToGroup.forEach((variableName: string) => {
						const variableInGroup = variablesMap[variableName];
						if (variableInGroup) {
							items.push({
								label: variableInGroup.label,
								value: variableInGroup.name,
								checked: false
							});
						}
					});
				}
			} else if ('set' in orderItem) {
				const series = seriesMap[orderItem.set];
				if (series?.aggregationRules) {
					series.aggregationRules.forEach((rule: AggregationRule) => {
						if (rule?.name) {
							items.push({
								label: `${
									variablesMap[rule.aggregator.variableName].label
								} (${rule.rule.type.toUpperCase()})`,
								value: rule.name,
								checked: false
							});
						}
					});
				}
			}
		});
		return items;
	}, [variablesData]);

	const [parsedMainVariableItems, setParsedMainVariableItems] =
		useState<DropdownItem[]>(mainVariableItems);

	function handleSelectSeriesItem(seriesNames: string[] | null | undefined) {
		if (!seriesNames || seriesNames.length === 0) {
			setParsedSeriesItems(state => state.map(item => ({ ...item, checked: false })));
		} else {
			setParsedSeriesItems(state =>
				state.map(item => ({
					...item,
					checked: seriesNames.includes(item.value)
				}))
			);
		}
	}

	function handleDeleteSelectedSeriesItem(seriesName: string) {
		const newParsedSeriesItems = parsedSeriesItems.map(item => ({
			...item,
			checked: item.value === seriesName ? false : item.checked
		}));
		setParsedSeriesItems(newParsedSeriesItems);
	}

	function handleSelectMainVariable(variableNames: string[] | null | undefined) {
		if (!variableNames || variableNames.length === 0) {
			setParsedMainVariableItems(state => state.map(item => ({ ...item, checked: false })));
		} else {
			setParsedMainVariableItems(state =>
				state.map(item => ({
					...item,
					checked: variableNames.includes(item.value)
				}))
			);
		}
	}

	function handleDeleteSelectedMainVariable(variableName: string) {
		const newParsedMainVariableItems = parsedMainVariableItems.map(item => ({
			...item,
			checked: item.value === variableName ? false : item.checked
		}));
		setParsedMainVariableItems(newParsedMainVariableItems);
	}

	function handleSubmit() {
		const checkedSeries = parsedSeriesItems.filter(item => item.checked);

		if (checkedSeries.length === 0) {
			return;
		}

		const selectedSeriesForExport: SeriesVariables[] = checkedSeries.map(item => ({
			seriesName: item.value,
			variables: null
		}));

		const selectedMainVariables = parsedMainVariableItems
			.filter(item => item.checked)
			.map(item => item.value);

		exportSeries({
			fileType,
			useCategoryLabels,
			timezone,
			splitMultipleCategories,
			includeSystemVariables,
			includeVariableTypes,
			seriesVariables: selectedSeriesForExport,
			mainVariables: selectedMainVariables
		});
	}

	const areFiltersActive = useMemo(() => {
		if (projectId && filtersStoreData.byProjectId[projectId]) {
			return filtersStoreData.byProjectId[projectId].active.length > 0;
		}
		return false;
	}, [projectId, filtersStoreData]);

	return (
		<Modal
			title={dictionary.exportSeries(projectId ? parseInt(projectId, 10) : undefined)}
			primary={{
				label: dictionary.export,
				loading: loadingExportSeries,
				onClick: handleSubmit,
				disabled: loadingExportSeries || isEmptySeriesSelection
			}}
			neutral={{
				label: dictionary.cancel,
				onClick: onClose
			}}
			fullSizeConfig={{
				narrow: true
			}}
			onClose={onClose}
			size={s => s.m}
			visible
			close
		>
			{loadingVariables ? (
				<Loader />
			) : (
				<>
					<Typography.Paragraph fontweight={fw => fw.bold}>
						{dictionary.chooseSeries}
					</Typography.Paragraph>
					<Spacer size={size => size.s} />
					<InputTagWithCheckboxDropdown
						items={parsedSeriesItems}
						onChange={values => handleSelectSeriesItem(values)}
						onDeleteAll={() => handleSelectSeriesItem(null)}
						onDelete={value => handleDeleteSelectedSeriesItem(value)}
						placeholder={dictionary.chooseSeriesPlaceholder}
					/>
					<Spacer size={size => size.m} />

					<Typography.Paragraph fontweight={fw => fw.bold}>
						{dictionary.chooseMainVariables}
					</Typography.Paragraph>
					<Spacer size={size => size.s} />
					<InputTagWithCheckboxDropdown
						items={parsedMainVariableItems}
						onChange={values => handleSelectMainVariable(values)}
						onDeleteAll={() => handleSelectMainVariable(null)}
						onDelete={value => handleDeleteSelectedMainVariable(value)}
						placeholder={dictionary.chooseMainVariablesPlaceholder}
						preserveOrder={true}
					/>
					<Spacer size={size => size.m} />

					<Typography.Paragraph fontweight={fw => fw.bold}>
						{dictionary.filters}
					</Typography.Paragraph>
					<Spacer size={size => size.xs} />
					<Typography.Paragraph>{dictionary.filtersHint}</Typography.Paragraph>
					<Spacer size={size => size.xs} />
					{areFiltersActive ? (
						<Typography.Paragraph fontweight={fw => fw.bold} color={Colors.text.error}>
							{dictionary.activeFilters}
						</Typography.Paragraph>
					) : (
						<Typography.Paragraph>{dictionary.noActiveFilters}</Typography.Paragraph>
					)}
					<Spacer size={size => size.s} />

					<Typography.Paragraph fontweight={fw => fw.bold}>
						{dictionary.fileType}
					</Typography.Paragraph>
					<Spacer size={size => size.xs} />
					<Flex align={a => a.center} justify={j => j.start}>
						<RadioButton
							inline
							label={'Excel'}
							onSelect={() => setFileType('xlsx')}
							selected={fileType === 'xlsx'}
							marginOffset={{ right: 1.6 }}
						/>
						<RadioButton
							inline
							label={'CSV'}
							onSelect={() => setFileType('csv')}
							selected={fileType === 'csv'}
						/>
					</Flex>
					<Spacer size={size => size.s} />

					<Typography.Paragraph fontweight={fw => fw.bold}>
						{dictionary.useCategoryLabels}
					</Typography.Paragraph>
					<Spacer size={size => size.xs} />
					<RadioButton
						label={dictionary.label}
						selected={useCategoryLabels}
						onSelect={() => setUseCategoryLabels(true)}
						description={dictionary.labelHint}
					/>
					<RadioButton
						label={dictionary.value}
						selected={!useCategoryLabels}
						onSelect={() => setUseCategoryLabels(false)}
						description={dictionary.valueHint}
					/>
					<Spacer size={size => size.s} />

					<Typography.Paragraph fontweight={fw => fw.bold}>
						{dictionary.timezone}
					</Typography.Paragraph>
					<Spacer size={size => size.xs} />
					<RadioButton
						label={dictionary.yes}
						selected={timezone === 'yes'}
						onSelect={() => setTimezone('yes')}
						description={dictionary.timezoneYesHint}
					/>
					<RadioButton
						label={dictionary.no}
						selected={timezone === 'no'}
						onSelect={() => setTimezone('no')}
						description={dictionary.timezoneNoHint}
					/>
					<RadioButton
						label={'UTC'}
						selected={timezone === 'utc'}
						onSelect={() => setTimezone('utc')}
						description={dictionary.timezoneUtcHint}
					/>
					<Spacer size={size => size.s} />

					<Typography.Paragraph fontweight={fw => fw.bold}>
						{dictionary.splitMultipleCategories}
					</Typography.Paragraph>
					<Spacer size={size => size.xs} />
					<Flex align={a => a.center} justify={j => j.start}>
						<RadioButton
							inline
							label={dictionary.no}
							onSelect={() => setSplitMultipleCategories(false)}
							selected={!splitMultipleCategories}
							marginOffset={{ right: 1.6 }}
						/>
						<RadioButton
							inline
							label={dictionary.yes}
							onSelect={() => setSplitMultipleCategories(true)}
							selected={splitMultipleCategories}
						/>
					</Flex>
					<Spacer size={size => size.s} />

					<Typography.Paragraph fontweight={fw => fw.bold}>
						{dictionary.includeSystemVariables}
					</Typography.Paragraph>
					<Spacer size={size => size.xs} />
					<Typography.Paragraph>
						{dictionary.includeSystemVariablesHint}
					</Typography.Paragraph>
					<Spacer size={size => size.xs} />
					<Flex align={a => a.center} justify={j => j.start}>
						<RadioButton
							inline
							label={dictionary.no}
							onSelect={() => setIncludeSystemVariables(false)}
							selected={!includeSystemVariables}
							marginOffset={{ right: 1.6 }}
						/>
						<RadioButton
							inline
							label={dictionary.yes}
							onSelect={() => setIncludeSystemVariables(true)}
							selected={includeSystemVariables}
						/>
					</Flex>
					<Spacer size={size => size.s} />

					<Typography.Paragraph fontweight={fw => fw.bold}>
						{dictionary.includeVariableTypes}
					</Typography.Paragraph>
					<Spacer size={size => size.xs} />
					<Typography.Paragraph>
						{dictionary.includeVariableTypesHint}
					</Typography.Paragraph>
					<Spacer size={size => size.xs} />
					<Flex align={a => a.center} justify={j => j.start}>
						<RadioButton
							inline
							label={dictionary.no}
							onSelect={() => setIncludeVariableTypes(false)}
							selected={!includeVariableTypes}
							marginOffset={{ right: 1.6 }}
						/>
						<RadioButton
							inline
							label={dictionary.yes}
							onSelect={() => setIncludeVariableTypes(true)}
							selected={includeVariableTypes}
						/>
					</Flex>
				</>
			)}
		</Modal>
	);
}

const DICTIONARY = {
	[LanguageType.English]: {
		exportSeries: (projectId?: number) =>
			projectId ? `Export series for project ${projectId}` : 'Export series',
		chooseSeries: 'Choose series',
		chooseSeriesPlaceholder: 'Select or search series',
		chooseMainVariables: 'Choose main level variables (optional)',
		chooseMainVariablesPlaceholder: 'Select or search variables',
		fileType: 'Which file type do you want to export?',
		useCategoryLabels: 'Use category labels or values?',
		timezone: 'Include timezone for datetime variables?',
		timezoneYesHint: 'Datetime variables with timezone (e.g. "2021-01-01T12:00:00+01:00").',
		timezoneNoHint:
			'Datetime variables without timezone in local time (e.g. "2021-01-01 12:00:00").',
		timezoneUtcHint:
			'Datetime variables without timezone in UTC time (e.g. "2021-01-01 11:00:00").',
		splitMultipleCategories: 'Split category multiple variables into separate columns?',
		includeSystemVariables: 'Include system variables?',
		includeVariableTypes: 'Include variable types in column names?',
		export: 'Export',
		cancel: 'Cancel',
		yes: 'Yes',
		no: 'No',
		label: 'Label',
		value: 'Value',
		labelHint: 'This option exports the value that is shown in the dataset table.',
		valueHint:
			'This option exports the alternative value that is configured in the variable settings.',
		filters: 'Filters',
		filtersHint:
			'Filters on entries that are applied on the dataset page will be applied to the exported dataset',
		activeFilters: 'You have active filters applied on the dataset',
		noActiveFilters: 'Currently no active filters',
		includeSystemVariablesHint:
			'System variables are variables that are not user-defined, such as entry ID, owned by, created by, etc.',
		includeVariableTypesHint:
			'With type hints, the columns will have a suffix indicating the type. For example, category#event. This makes it easier to reimport the data.'
	},
	[LanguageType.Norwegian]: {
		exportSeries: (projectId?: number) =>
			projectId ? `Eksporter serier for prosjekt ${projectId}` : 'Eksporter serier',
		chooseSeries: 'Velg serier',
		chooseSeriesPlaceholder: 'Velg eller søk etter serier',
		chooseMainVariables: 'Velg variabler fra hovednivået (valgfritt)',
		chooseMainVariablesPlaceholder: 'Velg eller søk etter variabler',
		fileType: 'Hvilken filtype vil du eksportere?',
		useCategoryLabels: 'Bruk kategorilabeller eller verdier?',
		timezone: 'Inkluder tidssone for dato og tid variabler?',
		timezoneYesHint: 'Dato og tid variabler med tidssone (f.eks. "2021-01-01T12:00:00+01:00").',
		timezoneNoHint:
			'Dato og tid variabler uten tidssone i lokal tid (f.eks. "2021-01-01 12:00:00").',
		timezoneUtcHint:
			'Dato og tid variabler uten tidssone i UTC-tid (f.eks. "2021-01-01 11:00:00").',
		splitMultipleCategories: 'Del multippel kategorier inn i separate kolonner?',
		includeSystemVariables: 'Inkluder systemvariabler?',
		includeVariableTypes: 'Inkluder variabeltyper i kolonnenavnene?',
		export: 'Eksporter',
		cancel: 'Avbryt',
		yes: 'Ja',
		no: 'Nei',
		label: 'Etikett',
		value: 'Verdi',
		labelHint: 'Dette valget eksporterer verdien som vises i datasettet.',
		valueHint:
			'Dette valget eksporterer den alternative verdien som er konfigurert i variabelinnstillingene.',
		filters: 'Filtre',
		filtersHint:
			'Filtre på oppføringer som er anvendt på datasettetsiden vil bli anvendt på det eksporterte datasettet',
		activeFilters: 'Du har aktive filtre på datasettet',
		noActiveFilters: 'Ingen aktive filtre',
		includeSystemVariablesHint:
			'Systemvariabler er variabler som ikke er definert av brukeren, som f.eks. oppførings-ID, eid av, opprettet av, osv.',
		includeVariableTypesHint:
			'Med variabeltyper i kolonnenavnene vil de ha et suffiks som indikerer typen. For eksempel, kategori#hendelse. Dette gjør det enklere å importere dataene igjen.'
	}
};
